'use strict';

/** Study builder — product fit options, therapeutic list, and quote readiness */

export const PRODUCT_FAMILIES = [
	{
		id: 'global',
		label: 'Global',
		description: 'Broad untargeted profiling — our flagship discovery offering.',
		pricingId: 'global-discovery',
		tileGradient: 'linear-gradient(135deg, #284A6F 0%, #3987ef 100%)',
	},
	{
		id: 'targeted',
		label: 'Targeted',
		description: 'Focused panels and targeted assays (e.g. MoA, efficacy, tox biomarkers).',
		pricingId: 'moa-discovery',
		tileGradient: 'linear-gradient(135deg, #855DA3 0%, #cc85ff 100%)',
	},
	{
		id: 'complex-lipid',
		label: 'Complex Lipid',
		description: 'Deep lipidomic coverage for complex lipid biology.',
		pricingId: 'global-discovery',
		tileGradient: 'linear-gradient(135deg, #001D38 0%, #284A6F 100%)',
	},
	{
		id: 'microbiome',
		label: 'Microbiome',
		description: 'Host–microbiome relevant metabolites (e.g. Microbiome SmartPanel).',
		pricingId: 'microbiome-smartpanel',
		tileGradient: 'linear-gradient(135deg, #12824a 0%, #57db8e 100%)',
	},
	{
		id: 'custom',
		label: 'Custom',
		description: 'Custom assay design — we will follow up with a tailored quote.',
		pricingId: 'global-discovery',
		tileGradient: 'linear-gradient(135deg, #FDA851 0%, #FF4F58 100%)',
	},
];

/** Shown as included for every study — layout in `ProductFitForm.jsx` (services step). */
export const SELF_SERVICE_BASE = {
	id: 'self-service-ibp',
	label: 'Self service (Integrated Bioinformatics Platform)',
	description: 'Base delivery included with all studies — exploration, downloads, and core workflows.',
};

export const PRODUCT_FINDER_INTENTS = [
	{
		id: 'broad',
		label: 'Broad discovery',
		blurb: 'Untargeted profiling across the metabolome',
		suggestFamilyId: 'global',
		tileLetter: '◇',
		tileGradient: 'linear-gradient(135deg, #284A6F 0%, #855DA3 100%)',
	},
	{
		id: 'focused',
		label: 'Focused assays',
		blurb: 'Panels, MoA, efficacy, or tox biomarkers',
		suggestFamilyId: 'targeted',
		tileLetter: '◎',
		tileGradient: 'linear-gradient(135deg, #855DA3 0%, #FF4F58 100%)',
	},
	{
		id: 'microbiome',
		label: 'Microbiome',
		blurb: 'Host–microbe metabolism',
		suggestFamilyId: 'microbiome',
		tileLetter: '☘',
		tileGradient: 'linear-gradient(135deg, #0e673b 0%, #57db8e 100%)',
	},
	{
		id: 'lipid',
		label: 'Lipid-heavy biology',
		blurb: 'Complex lipid coverage',
		suggestFamilyId: 'complex-lipid',
		tileLetter: '◈',
		tileGradient: 'linear-gradient(135deg, #001D38 0%, #60759f 100%)',
	},
	{
		id: 'unsure',
		label: 'Not sure yet',
		blurb: 'We will help you scope',
		suggestFamilyId: 'custom',
		tileLetter: '?',
		tileGradient: 'linear-gradient(135deg, #60759f 0%, #D3DFE7 100%)',
	},
];

export const THERAPEUTIC_AREAS = [
	{ id: 'arthritis', label: 'Arthritis' },
	{ id: 'autoimmune-disease', label: 'Autoimmune disease' },
	{ id: 'bacterial-infection', label: 'Bacterial infection' },
	{ id: 'blood-disease', label: 'Blood disease' },
	{ id: 'bone-disease', label: 'Bone disease' },
	{ id: 'cancer', label: 'Cancer' },
	{ id: 'cardiovascular-disease', label: 'Cardiovascular disease' },
	{ id: 'endocrine-disease', label: 'Endocrine disease' },
	{ id: 'eye-disease', label: 'Eye disease' },
	{ id: 'female-health', label: 'Female health' },
	{ id: 'fungal-disease', label: 'Fungal disease' },
	{ id: 'gastrointestinal-disease', label: 'Gastrointestinal disease' },
	{ id: 'kidney-disease', label: 'Kidney disease' },
	{ id: 'liver-disease', label: 'Liver disease' },
	{ id: 'male-health', label: 'Male health' },
	{ id: 'metabolic-disease', label: 'Metabolic disease' },
	{ id: 'musculoskeletal-disease', label: 'Musculoskeletal disease' },
	{ id: 'neurological-disease', label: 'Neurological disease' },
	{ id: 'not-applicable', label: 'Not applicable' },
	{ id: 'oral-health', label: 'Oral health' },
	{ id: 'other-therapeutic', label: 'Other' },
	{ id: 'pancreas-disease', label: 'Pancreas disease' },
	{ id: 'parasitic-infection', label: 'Parasitic infection' },
	{ id: 'respiratory-disease', label: 'Respiratory disease' },
	{ id: 'skin-disease', label: 'Skin disease' },
	{ id: 'viral-infection', label: 'Viral infection' },
];

export const STUDY_GOALS = [
	{ id: 'toxicology', label: 'Toxicology & safety' },
	{ id: 'moa', label: 'Mechanism / target validation' },
	{ id: 'biomarker-discovery', label: 'Biomarker discovery' },
	{ id: 'efficacy-pharmacodynamic', label: 'Efficacy / pharmacodynamic' },
	{ id: 'metabolism-pk', label: 'Metabolism & pharmacokinetics' },
	{ id: 'microbiome-host', label: 'Microbiome / host–microbe' },
	{ id: 'exploratory', label: 'Exploratory profiling' },
	{ id: 'other', label: 'Other' },
];

export const STUDY_SIZES = [
	{ id: '1-30', label: '1–30 samples', displayPrimary: '1–30', displayHint: 'Pilot / small' },
	{ id: '31-120', label: '31–120 samples', displayPrimary: '31–120', displayHint: 'Mid-size' },
	{ id: '121-500', label: '121–500 samples', displayPrimary: '121–500', displayHint: 'Core studies' },
	{ id: '501-1000', label: '501–1,000 samples', displayPrimary: '501–1k', displayHint: 'Large cohort' },
	{ id: '1000+', label: '1,000+ samples', displayPrimary: '1k+', displayHint: 'Program scale' },
];

/** Species shown as quick tiles when present in the product matrix */
export const COMMON_SPECIES_TILES = [
	'Mammal',
	'Microbe',
	'Non-Mammal',
	'Plant',
	'Other',
];

export const SPECIES_OTHER = 'Other';

export const MATRIX_OPTIONS_BY_SPECIES = {
	Mammal: ['Human', 'Cow', 'Dog', 'Horse', 'Cat', 'Marine mammal', 'Pig', 'Rabbit', 'Sheep', 'Non-human primate', 'Hamster', 'Rat', 'Squirrel', 'Mouse'],
	Microbe: ['Cow', 'Dog', 'Horse', 'Cat'],
	'Non-Mammal': ['Marine mammal', 'Pig', 'Rabbit', 'Sheep', 'Non-human primate', 'Hamster', 'Rat', 'Squirrel'],
	Plant: ['Algae', 'Arabidopsis', 'Brassica', 'Cacao', 'Camelina', 'Cucumber', 'Grape', 'Corn', 'Orange', 'Peanut', 'Pine', 'Rice', 'Sorghum', 'Soybean', 'Sugarcane', 'Tobacco', 'Tomato', 'Walnut', 'Wheat'],
	Other: [],
};

export const MATRIX_CATEGORIES = [
	'Plasma',
	'Serum',
	'Tissue',
	'Fluid',
	'Urine',
	'Cells',
	'Feces',
	'Plant Tissue',
	'Tissue Extract',
	'Cell Extract',
	'CSF',
	'Blood',
	'Tumor Tissue',
	'Whole Organism',
	'Solid material',
	'Skin Tape',
	'Organoid',
	'Other',
];

export const MATRIX_TYPES_BY_CATEGORY = {
	Plasma: ['EDTA plasma', 'Heparinized plasma', 'Citrate plasma', 'Peripheral collection device'],
	Serum: ['Serum', 'Card', 'Peripheral collection device'],
	Tissue: ['Adipose', 'Bone', 'Bone marrow', 'Brain, other', 'Brain, cerebellum', 'Brain, cortex', 'Brain, whole', 'Breast', 'Embryo', 'Endometrium', 'Epithelium', 'Hair follicle', 'Heart', 'Intestine, other', 'Intestine, cecum', 'Intestine, large', 'Intestine, rectum', 'Intestine, small', 'Kidney', 'Liver', 'Lung', 'Mixed', 'Muscle', 'Nerve', 'Ocular tissue, lens', 'Ocular tissue, retina', 'Ovary', 'Pancreas', 'Placenta', 'Prostate', 'Skin', 'Spinal cord', 'Spleen', 'Testis', 'Xenograft'],
	Fluid: ['Amniotic fluid', 'Aqueous humor', 'Milk', 'Bronchoalveolar lavage (BALF)', 'Dialysate', 'Hydrolysate', 'Intestinal fluid', 'Lung', 'Lymph', 'Placenta', 'Saliva', 'Solution', 'Sweat', 'Tears', 'Semen', 'Vaginal swab', 'Nasal secretion', 'Synovial fluid', 'Brain fluid', 'Ascites', 'Bile', 'Breath condensate', 'Follicular', 'Perfusate', 'Vitreous fluid', 'Juice', 'Mucosal epithelia', 'Saline solution', 'Hemolymph', 'Cell media'],
	Urine: ['DRE urine', 'Urine', 'DRE sediment'],
	Cells: ['Algae', 'Bacteria', 'CHO', 'Hepatocytes', 'Leukocytes', 'Macrophage', 'Mammalian (non-CHO)', 'Yeast', 'Fungus'],
	Feces: ['Frozen feces', 'OmniMet', 'Fecal card'],
	'Plant Tissue': ['Ear', 'Hydrolysate', 'Leaf', 'Mixed', 'Root', 'Seed', 'Shank', 'Stem', 'Shoot', 'Flower', 'Phloem', 'Fruit'],
	'Tissue Extract': ['Bone', 'Bone marrow', 'Brain', 'Breast', 'Colon', 'Embryo', 'Epithelium', 'Intestine', 'Kidney', 'Liver', 'Lung', 'Lymph', 'Muscle', 'Nerve', 'Ovary', 'Pancreas', 'Prostate', 'Skin'],
	'Cell Extract': ['Bacteria', 'CHO', 'Hepatocytes', 'Macrophage', 'Stem', 'Yeast', 'Fungus', 'Mycobacteria', 'Algae', 'Leukocytes', 'Mammalian (non-CHO)', 'Organelle'],
	CSF: ['CSF'],
	Blood: ['Dried blood spot (DBS)', 'Cord blood', 'Platelets', 'Red blood cells', 'Whole blood', 'Peripheral collection device'],
	'Tumor Tissue': ['Brain', 'Intestine', 'Muscle', 'Skin', 'Breast', 'Kidney'],
	'Whole Organism': ['Drosophila, adult', 'Drosophila, larvae', 'Insect, adult', 'Nematode', 'Insect, larvae'],
	'Solid material': ['Dog Food', 'Cat Food', 'Food', 'Soil'],
	'Skin Tape': ['Sebutape', 'D-squame tape'],
	Organoid: ['Tissue', 'Organoid media'],
	Other: ['Other'],
};

/**
 * Fresh default for study builder fit-step selections (product line through scope).
 * @returns {Record<string, unknown>}
 */
export function getInitialStudyBuilderSelections() {
	return {
		projectName: '',
		productFamily: null,
		statsServices: null,
		interpretationLevel: null,
		therapeuticAreas: [],
		therapeuticAreaOther: '',
		studyGoal: null,
		studyGoalOther: '',
		studySize: null,
		sampleCountMode: 'tier',
		exactSampleCount: 60,
		matrixScopeMode: 'single',
		numberOfMatrices: null,
		sampleLines: [{ id: 'sl-1', count: '', species: null, matrix: null, matrixCategory: null, matrixType: null, otherNotes: '' }],
	};
}

/**
 * Default sample count hint for a study size tier (indicative quote).
 * @param {string} [studySizeId]
 * @returns {number}
 */
export function defaultSampleCountForStudySize(studySizeId) {
	const map = { '1-30': 15, '31-120': 60, '121-500': 250, '501-1000': 750, '1000+': 1200 };
	return map[studySizeId] ?? 250;
}

/**
 * Study size tier for pricing — uses an explicit selection when present, otherwise infers from sample count.
 * @param {string|null|undefined} explicitStudySizeId
 * @param {number} sampleCount
 * @returns {string}
 */
export function resolveStudySizeId(explicitStudySizeId, sampleCount) {
	if (explicitStudySizeId) return explicitStudySizeId;
	const n = Math.round(Number(sampleCount));
	if (!Number.isFinite(n) || n < 1) return '121-500';
	const order = ['1-30', '31-120', '121-500', '501-1000', '1000+'];
	for (const tierId of order) {
		const { lo, hi } = getTierSampleBounds(tierId);
		if (n >= lo && n <= hi) return tierId;
	}
	return '1000+';
}

/**
 * Numeric sample bounds for a study size tier (used for tier-based quote ranges and exact-count slider).
 * @param {string|null|undefined} studySizeId
 * @returns {{ lo: number, hi: number }}
 */
export function getTierSampleBounds(studySizeId) {
	const map = {
		'1-30': [1, 30],
		'31-120': [31, 120],
		'121-500': [121, 500],
		'501-1000': [501, 1000],
		'1000+': [1000, 5000],
	};
	const pair = studySizeId ? map[studySizeId] : null;
	if (!pair) return { lo: 1, hi: 500 };
	return { lo: pair[0], hi: pair[1] };
}

/**
 * Total sample count used for study-size tier checks on the scope step.
 * @param {Record<string, unknown>} s
 * @returns {number}
 */
export function getScopeSampleTotal(s) {
	if (!s || typeof s !== 'object') return 0;
	const lines = Array.isArray(s.sampleLines) ? s.sampleLines : [];
	const scopeMode = s.matrixScopeMode || 'single';
	if (scopeMode === 'single' && (s.sampleCountMode || 'tier') === 'exact') {
		const e = Number(s.exactSampleCount);
		if (Number.isFinite(e)) return Math.round(e);
	}
	return lines.reduce((sum, ln) => sum + (parseInt(String(ln?.count ?? '').replace(/,/g, ''), 10) || 0), 0);
}

/**
 * When total is unset (below 1), line-level validation applies. Otherwise total must fall within the study tier band.
 * @param {Record<string, unknown>} s
 * @returns {boolean}
 */
export function isScopeTotalWithinStudyTier(s) {
	if (!s?.studySize) return true;
	const total = getScopeSampleTotal(s);
	if (!Number.isFinite(total) || total < 1) return true;
	const { lo, hi } = getTierSampleBounds(s.studySize);
	return total >= lo && total <= hi;
}

/**
 * @param {Record<string, unknown>} s
 * @returns {string|null}
 */
export function getScopeTierTotalWarningMessage(s) {
	if (!s?.studySize || isScopeTotalWithinStudyTier(s)) return null;
	const { lo, hi } = getTierSampleBounds(s.studySize);
	const total = getScopeSampleTotal(s);
	const sizeLabel = STUDY_SIZES.find((z) => z.id === s.studySize)?.label || String(s.studySize);
	return `Your total of ${total} samples is outside the ${sizeLabel} range (${lo}–${hi} samples). Adjust your counts so the total is within that range before continuing.`;
}

/**
 * @param {unknown} rawCount
 * @returns {string|null} Error message for empty or non–positive-integer sample counts; null when valid.
 */
export function getSampleCountInputError(rawCount) {
	const raw = String(rawCount ?? '').trim();
	if (!raw) return 'Enter a number of samples';
	const n = parseInt(raw.replace(/,/g, ''), 10);
	if (!Number.isFinite(n) || n < 1) return 'Enter a valid number of samples (at least 1)';
	return null;
}

/**
 * @param {Record<string, unknown>} line
 * @param {'single' | 'unsure'} [lineMode] — per-line rules; use `'unsure'` for approximate + notes only
 * @returns {boolean}
 */
export function isSampleLineComplete(line, lineMode = 'single') {
	const n = parseInt(String(line?.count ?? '').replace(/,/g, ''), 10);
	if (!Number.isFinite(n) || n <= 0) return false;
	if (lineMode === 'unsure') {
		return String(line?.otherNotes || '').trim().length >= 8;
	}
	if (!line?.species) return false;
	if (line.species === SPECIES_OTHER) return Boolean(String(line.otherNotes || '').trim());
	if (!line.matrix) return false;
	if (!line.matrixCategory) return false;
	if (!line.matrixType) return false;
	return true;
}

/**
 * Specific blockers for a single sample line (single-matrix or unsure scope).
 * @param {Record<string, unknown>|undefined} line
 * @param {'single' | 'unsure'} [lineMode]
 * @returns {string[]}
 */
export function getSampleLineIncompleteReasons(line, lineMode = 'single') {
	const reasons = [];
	const countError = getSampleCountInputError(line?.count);
	if (countError) {
		reasons.push(countError.endsWith('.') ? countError : `${countError}.`);
	}
	if (lineMode === 'unsure') {
		if (String(line?.otherNotes || '').trim().length < 8) {
			reasons.push('Tell us what we should know (at least 8 characters).');
		}
		return reasons;
	}
	if (!line?.species) {
		reasons.push('Select a species.');
		return reasons;
	}
	if (line.species === SPECIES_OTHER) {
		if (!String(line.otherNotes || '').trim()) {
			reasons.push('Describe species and matrix for "Other".');
		}
		return reasons;
	}
	if (!line.matrix) reasons.push('Select an organism.');
	if (!line.matrixCategory) reasons.push('Select a matrix.');
	if (!line.matrixType) reasons.push('Select a matrix type.');
	return reasons;
}

function hasValidExactSampleCount(s) {
	if ((s.sampleCountMode || 'tier') !== 'exact') return true;
	const e = Number(s.exactSampleCount);
	if (!Number.isFinite(e) || e < 1) return false;
	const { lo, hi } = getTierSampleBounds(s.studySize);
	return e >= lo && e <= hi;
}

export function isServicesStepComplete(s) {
	if (s.statsServices !== 'yes' && s.statsServices !== 'no') return false;
	return s.statsServices !== 'yes' || ['none', 'standard', 'advanced'].includes(s.interpretationLevel);
}

export function isTherapeuticStepComplete(s) {
	const tas = s.therapeuticAreas;
	return (
		Array.isArray(tas) &&
		tas.length > 0 &&
		tas.length <= 3 &&
		(!tas.includes('other-therapeutic') || Boolean(String(s.therapeuticAreaOther || '').trim()))
	);
}

export function isGoalStepComplete(s) {
	return Boolean(s.studyGoal) && (s.studyGoal !== 'other' || Boolean(String(s.studyGoalOther || '').trim()));
}

export function isScopeStepComplete(s) {
	const lines = Array.isArray(s.sampleLines) ? s.sampleLines : [];
	if (lines.length === 0) return false;
	const scopeMode = s.matrixScopeMode || 'single';
	if (scopeMode === 'multiple' || scopeMode === 'unsure') {
		const numMatrices = parseInt(String(s.numberOfMatrices ?? '').replace(/,/g, ''), 10);
		if (!Number.isFinite(numMatrices) || numMatrices < 1) return false;
		if (lines.length !== 1 || !isSampleLineComplete(lines[0], 'unsure')) return false;
	} else if (lines.length !== 1 || !isSampleLineComplete(lines[0], 'single')) {
		return false;
	}
	return true;
}

/**
 * Human-readable blockers for continuing from the fit flow (study size, scope, prior steps).
 * @param {Record<string, unknown>} s
 * @returns {string[]}
 */
export function getFitIncompleteReasons(s) {
	const reasons = [];
	if (!s?.productFamily) {
		reasons.push('Choose a product line.');
	}
	if (!isServicesStepComplete(s)) {
		if (s.statsServices !== 'yes' && s.statsServices !== 'no') {
			reasons.push('Choose whether you want statistics services on this study.');
		} else if (s.statsServices === 'yes' && !['none', 'standard', 'advanced'].includes(s.interpretationLevel)) {
			reasons.push('Choose an interpretation level, or select "No interpretation", when statistics are enabled.');
		}
	}
	if (!isTherapeuticStepComplete(s)) {
		const tas = s.therapeuticAreas;
		if (!Array.isArray(tas) || tas.length === 0) {
			reasons.push('Select at least one therapeutic area (up to three).');
		} else if (tas.length > 3) {
			reasons.push('Select at most three therapeutic areas.');
		} else if (tas.includes('other-therapeutic') && !String(s.therapeuticAreaOther || '').trim()) {
			reasons.push('Describe your "Other" therapeutic area.');
		}
	}
	if (!isGoalStepComplete(s)) {
		if (!s.studyGoal) {
			reasons.push('Choose what you are trying to answer for this study.');
		} else if (s.studyGoal === 'other' && !String(s.studyGoalOther || '').trim()) {
			reasons.push('Enter text for your custom study goal.');
		}
	}
	{
		const lines = Array.isArray(s.sampleLines) ? s.sampleLines : [];
		const scopeMode = s.matrixScopeMode || 'single';
		if (lines.length === 0) {
			reasons.push('Add sample lines with counts (and species/matrix) for your scope.');
		} else if (scopeMode === 'multiple' || scopeMode === 'unsure') {
			const numMatrices = parseInt(String(s.numberOfMatrices ?? '').replace(/,/g, ''), 10);
			if (!Number.isFinite(numMatrices) || numMatrices < 1) {
				reasons.push('Enter the number of matrices.');
			}
			const count = parseInt(String(lines[0]?.count ?? '').replace(/,/g, ''), 10);
			if (!Number.isFinite(count) || count < 1) {
				reasons.push('Enter an approximate number of samples.');
			}
			const notes = String(lines[0]?.otherNotes || '').trim();
			if (notes.length < 8) {
				reasons.push('Tell us what we should know (at least 8 characters).');
			}
		} else if (lines.length !== 1 || !isSampleLineComplete(lines[0], 'single')) {
			reasons.push(...getSampleLineIncompleteReasons(lines[0], 'single'));
		}
	}
	return reasons;
}

/**
 * @param {Record<string, unknown>} s
 * @returns {boolean}
 */
export function isFitComplete(s) {
	return getFitIncompleteReasons(s).length === 0;
}

/**
 * @param {Record<string, unknown>} s
 * @returns {{ id: string, name: string, description?: string } | null}
 */
export function getQuoteProduct(s) {
	const fam = PRODUCT_FAMILIES.find((p) => p.id === s.productFamily);
	if (!fam) return null;
	return { id: fam.pricingId, name: fam.label, description: fam.description };
}

/**
 * @param {Record<string, unknown>} s
 * @returns {number} 0–1 completion for progress UI
 */
export function getFitCompletionRatio(s) {
	const steps = [
		Boolean(s.productFamily),
		isServicesStepComplete(s),
		isTherapeuticStepComplete(s),
		isGoalStepComplete(s),
		isScopeStepComplete(s),
	];
	return steps.filter(Boolean).length / steps.length;
}

export const PRODUCT_FIT_INTRO = {
	title: 'Find the right product for you.',
	subtitle:
		'Whether you\'re an emerging biotech, an established pharma company, or a contract research organization, we\'ll help you build a study that meets your needs.',
};
